/**
 * 流水**批量选择**的纯逻辑层（批 `107` · K3 · `AC-C1.19`–`C1.24`）
 *
 * 依据：C 域 PRD **§5.1.5**（批量动作与分级授权）· **§5.1.9**（合计条）·
 * `AC-C1.19`–`AC-C1.24` · 铁律 24（同一机制一处实现）· 铁律 59（不得自写第二份聚合）。
 *
 * ## 🔴 本文件**不实现**什么（三条，都是刻意的）
 *
 * | 不实现 | 为什么 |
 * |---|---|
 * | **合计金额** | 唯一实现在共享层 `nucleus/txn-summary.ts` 的 `summarizeSelection()` —— 本文件只做「行视图 → 内核入参」的形状适配（`legs` 逐条透传 + 交易级标记落到腿上），**一分钱都不加** |
 * | **"哪些动作对这笔可用"** | 分级授权（`AC-C1.20`：结构敏感动作只对单腿放行）的判定在**服务端**（`transaction-write.service.ts`），且**逐条带原因**返回（`AC-C1.21`）。端侧再造一份"多腿 ⇒ 置灰"的规则 = 同一判据两处实现，服务端改判据时端侧会**静默说谎** |
 * | **"这笔是不是支出"** | `isCountedInStats()` 是唯一判据（`nucleus/txn-row.ts`），合计内核已调它 |
 *
 * ## 🔴 选中态以 `transactionId` 为准（不是行下标）
 *
 * 翻页 / 刷新 / 加载更多之后，行下标会整体位移 —— 用下标做选中态会出现
 * 「勾了 A 却删了 B」这种**不可复现**的事故。本文件所有函数都以 `id` 为唯一键。
 */

import {
  summarizeSelection,
  type BulkMutationResultView,
  type BulkPatchResultView,
  type SelectionSummary,
  type SelectionTxn,
  type TransactionRowView,
} from '@jiaying/shared';

/** 空选中集（**共享同一个实例**，避免每次渲染都造一个新 `Set` 让 memo 失效） */
export const EMPTY_SELECTION: ReadonlySet<string> = new Set<string>();

/** 点一行：已选则取消、未选则加入（返回**新** Set —— React 状态不可原地改） */
export function toggleSelection(ids: ReadonlySet<string>, id: string): Set<string> {
  const next = new Set(ids);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  return next;
}

/** 「全选本页」= 把**当前已加载**的行并进选中集（不清掉之前翻页勾过的 —— 那是用户已经做过的决定） */
export function selectAllLoaded(ids: ReadonlySet<string>, loadedIds: readonly string[]): Set<string> {
  const next = new Set(ids);
  for (const id of loadedIds) next.add(id);
  return next;
}

/** 当前已加载行是否**全部**在选中集里（决定「全选本页」的勾选态） */
export function isAllLoadedSelected(ids: ReadonlySet<string>, loadedIds: readonly string[]): boolean {
  if (loadedIds.length === 0) return false;
  return loadedIds.every((id) => ids.has(id));
}

/**
 * 把选中的行视图适配成合计内核的入参。
 *
 * 🔴 唯一的"加工"是**交易级标记落到腿上**（`txnExcludeFromStats ← row.excludeFromStats`）：
 * 内核的 `isCountedInStats()` 取 `腿级 ∨ 交易级`，而行视图把两个标记分放在
 * `row.excludeFromStats` 与 `legs[].excludeFromStats` 两处 ⇒ 这里只做**搬运**，
 * 不重新判定"算不算收支"。少了这一步，"整笔不计收支"的交易会被算进合计（口径错但无人报错）。
 */
export function toSelectionTxns(
  rows: readonly TransactionRowView[],
  selected: ReadonlySet<string>,
): SelectionTxn[] {
  const out: SelectionTxn[] = [];
  for (const row of rows) {
    if (!selected.has(row.id)) continue;
    out.push({
      id: row.id,
      legs: row.legs.map((l) => ({
        id: l.id,
        accountId: l.accountId,
        currency: l.currency,
        direction: l.direction,
        amount: l.amount as never,
        categoryId: l.categoryId,
        categoryKind: l.categoryKind,
        excludeFromStats: l.excludeFromStats,
        txnExcludeFromStats: row.excludeFromStats,
      })),
    });
  }
  return out;
}

/** 合计条（**唯一实现是内核**，本函数只是三行适配） */
export function summarizeSelected(
  rows: readonly TransactionRowView[],
  selected: ReadonlySet<string>,
): SelectionSummary {
  return summarizeSelection(toSelectionTxns(rows, selected));
}

// ─────────────────────── 结果文案（逐条拒因原样展示） ───────────────────────

/**
 * 把批量结果拼成一句人话（`AC-C1.21`：**禁止静默失败**）。
 *
 * - 全成功 ⇒ 「已成功 N 笔」
 * - 有被拒 ⇒ 「成功 8 笔 / 失败 2 笔：原因A（1 笔）、原因B（1 笔）」
 *
 * 🔴 **原因文本逐字来自服务端** —— 这里只做**去重与计数**，不改写、不浓缩、不翻译。
 * 端侧自己写一句"部分失败"等于把"为什么"藏起来（`AC-C1.21` 明禁）。
 */
export function describeBulkResult(result: BulkPatchResultView | BulkMutationResultView): string {
  const ok = 'applied' in result ? result.applied : result.affected;
  const rejected = 'rejected' in result ? result.rejected : [];
  // 删除 / 恢复路径用 `skipped`（已在目标状态、本次未变动 —— 幂等友好，不算失败）
  const skipped = 'skipped' in result ? result.skipped : 0;

  const parts: string[] = [];
  parts.push(skipped > 0 ? `成功 ${String(ok)} 笔 / 未变动 ${String(skipped)} 笔` : `成功 ${String(ok)} 笔`);
  if (rejected.length > 0) {
    const byReason = new Map<string, number>();
    for (const r of rejected) byReason.set(r.reason, (byReason.get(r.reason) ?? 0) + 1);
    const detail = [...byReason.entries()]
      .map(([reason, n]) => `${reason}（${String(n)} 笔）`)
      .join('；');
    parts.push(`失败 ${String(rejected.length)} 笔：${detail}`);
  }
  return parts.join(' · ');
}

/**
 * 选中集里包含多少**多腿**（转账 / 工资条 / 拆分）交易 —— 只用于给一条**说明性提示**。
 *
 * 🔴 这不是"权限判定"：动作按钮**不置灰**（`AC-C1.20` 的置灰由服务端拒绝 + 理由落地在本批，
 * 见 changelog 的口径说明）。它只告诉用户"这类笔会被服务端按结构拒绝，原因是…"，
 * 免得点完才发现一半没改成。
 */
export function multiLegCount(rows: readonly TransactionRowView[], selected: ReadonlySet<string>): number {
  let n = 0;
  for (const row of rows) {
    if (selected.has(row.id) && row.legs.length > 1) n += 1;
  }
  return n;
}
